/**
 * WeaveIn Verify — the staff API (backend app/routers/verify.py, gated on the
 * `hr1_verify` capability) and the shapes it returns.
 *
 * Every call goes through authFetch so the active org (X-CBO-ID) is the one the
 * backend scopes to. Errors keep the backend's own words: a 409 on "qualify"
 * carries the list of elements still missing a named check, and the page shows it.
 */

import { authFetch } from '@/lib/authFetch';

export const ACTIVITY_TYPES = [
  'employment',
  'volunteer',
  'community_service',
  'job_training',
  'education',
  'job_search',
] as const;
export type ActivityType = (typeof ACTIVITY_TYPES)[number];

export const SITE_ELEMENTS = [
  'qualifying_basis',
  'evidence',
  'oversight_method',
  'point_of_contact',
  'activity_types',
] as const;
export type SiteElement = (typeof SITE_ELEMENTS)[number];

export const QUALIFYING_BASES = [
  'nonprofit_501c3',
  'government',
  'faith_based',
  'educational',
  'job_training',
  'tribal',
  'other',
] as const;

export const OVERSIGHT_METHODS = [
  'sign_in_sheet',
  'staff_dashboard',
  'qr_checkin',
  'roster',
  'supervisor_attestation',
] as const;

export const DISCOVERY = ['member_invitation', 'outreach', 'partner_referral', 'self_signup'] as const;

export const EXEMPTION_TYPES = [
  'pregnant_or_postpartum',
  'parent_caregiver_child_13_or_under',
  'caregiver_disabled_individual',
  'medically_frail',
  'disabled_veteran',
  'sud_treatment',
  'student_half_time',
  'meets_snap_tanf_work_rules',
  'tribal_member',
  'recently_incarcerated',
  'age_under_19_or_over_64',
] as const;

export type RecordStatus = 'captured' | 'confirmed' | 'self_attested' | 'disputed' | 'voided';
export type RecordSource = 'staff_entry' | 'qr_checkin' | 'sheet_photo' | 'roster' | 'member_self';
export type StatementState =
  | 'complete'
  | 'meets_needs_evidence'
  | 'awaiting_confirmation'
  | 'short_at_deadline'
  | 'at_risk'
  | 'in_progress';

export interface SiteCheck {
  id: number;
  element: SiteElement;
  criteria_ref: string | null;
  notes: string | null;
  checked_at: string;
  checked_by_name: string | null;
  checked_by_org: string | null;
}

export interface Site {
  id: number;
  name: string;
  host_cbo_id: number | null;
  host_org_name: string | null;
  registered_by_cbo_id: number;
  registered_by_org_name: string | null;
  street_address: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  contact_name: string | null;
  contact_phone: string | null;
  contact_email: string | null;
  qualifying_basis: string | null;
  qualifying_evidence: string | null;
  oversight_method: string | null;
  activity_types: ActivityType[];
  status: 'invited' | 'in_review' | 'qualified' | 'suspended';
  discovered_via: string;
  qualified_at: string | null;
  created_at: string;
  checks: SiteCheck[];
  missing_checks: SiteElement[];
  can_manage: boolean;
  can_capture: boolean;
  checkin_url?: string;
  /** Optional PNG data URL of the check-in QR, when the backend supplies one. */
  checkin_qr?: string;
}

export interface VerifyRecord {
  id: number;
  site_id: number | null;
  site_name: string | null;
  site_cbo_id: number | null;
  member_id: number | null;
  enrollment_id: number | null;
  participant_first_name: string;
  participant_last_name: string;
  participant_dob: string | null;
  participant_phone: string | null;
  activity_type: ActivityType;
  service_description: string;
  activity_date: string;
  hours: number;
  point_of_contact_name: string | null;
  point_of_contact_phone: string | null;
  source: RecordSource;
  filed_by_label: string;
  filed_by_name: string | null;
  status: RecordStatus;
  confirmed_at: string | null;
  confirmed_by_name: string | null;
  sheet_batch_id: number | null;
  extraction_confidence: number | null;
  matched_at: string | null;
  dispute_reason: string | null;
  disputed_at: string | null;
  resolution: string | null;
  resolution_notes: string | null;
  resolved_at: string | null;
  void_reason: string | null;
  delivered_at: string | null;
  missing_fields: string[];
  can_confirm?: boolean;
  can_resolve?: boolean;
}

export interface Statement {
  year: number;
  month: number;
  period_start: string;
  period_end: string;
  days_until_deadline: number;
  required_hours: number;
  counted_hours: number;
  verified_hours: number;
  self_attested_hours: number;
  pending_hours: number;
  disputed_hours: number;
  shortfall_hours: number;
  hours_by_type: Record<string, number>;
  meets_requirement: boolean;
  record_complete: boolean;
  incomplete_lines: number;
  counted_lines: number;
  sites: string[];
  state: StatementState;
  lines?: VerifyRecord[];
  delivered?: { total_hours: number; delivered_at: string; delivered_by_name?: string | null } | null;
  delivery_stale?: boolean;
}

export interface Enrollment {
  id: number;
  member_id: number | null;
  enrollment_number: string;
  first_name: string;
  last_name: string;
  date_of_birth: string | null;
  state_code: string | null;
  status: string;
  work_requirement_status: string | null;
  work_requirement_start_date: string | null;
  next_redetermination_date: string | null;
  last_redetermination_date: string | null;
  is_compliant: boolean | null;
  verification_date: string | null;
  program_id: number | null;
  program_name: string | null;
}

export interface EnrollmentRow extends Enrollment {
  statement: Statement;
  delivered: { total_hours: number; delivered_at: string } | null;
}

export interface Exemption {
  id: number;
  exemption_type: string;
  exemption_reason: string | null;
  start_date: string;
  end_date: string | null;
  status: string;
  documentation_required: boolean;
  documentation_received: boolean;
  review_notes: string | null;
  created_at: string;
}

export interface ReadinessMonth {
  year: number;
  month: number;
  counted_hours: number;
  meets_requirement: boolean;
  record_complete: boolean;
  state: StatementState;
  delivered: boolean;
  delivered_hours: number | null;
  delivery_stale: boolean;
}

export interface Readiness {
  due_date: string | null;
  redetermination: {
    id: number;
    redetermination_number: number;
    due_date: string;
    status: string;
    work_hours_verified: boolean | null;
    work_verification_date: string | null;
  } | null;
  lookback_months: ReadinessMonth[];
  ready: boolean;
}

export interface EnrollmentDetail {
  enrollment: Enrollment;
  statement: Statement;
  exemptions: Exemption[];
  exempt: boolean;
  readiness: Readiness;
  history: (Omit<Statement, 'lines'> & { delivered: boolean })[];
}

export interface SheetRow {
  line: number;
  first_name: string | null;
  last_name: string | null;
  date_of_birth: string | null;
  phone: string | null;
  activity_date: string | null;
  time_in: string | null;
  time_out: string | null;
  hours: number | null;
  activity_type: ActivityType | null;
  service_description: string | null;
  signature_present: boolean;
  confidence: number;
  unclear: string | null;
  needs_review: boolean;
}

export interface SheetBatch {
  id: number;
  site_id: number;
  site_name: string;
  file_name: string | null;
  mime_type: string;
  status: 'review' | 'confirmed' | 'failed' | 'discarded';
  extraction: {
    sheet?: {
      organization_seen: string | null;
      date_seen: string | null;
      activity_seen: string | null;
      supervisor_seen: string | null;
    };
    rows?: SheetRow[];
    error?: string;
  };
  model: string | null;
  created_at: string;
  confirmed_at: string | null;
  uploaded_by_name: string | null;
}

export interface Overview {
  registry: { total: number; qualified: number; pending: number; via_member_invitation: number };
  as_site: {
    awaiting_confirmation: number;
    disputed: number;
    confirmed: number;
    confirmed_hours: number;
    from_sheets: number;
    from_qr: number;
  };
  enrollments: { tracked: number; this_month: Partial<Record<StatementState, number>> };
  disputes_open: number;
  year: number;
  month: number;
}

export interface MemberHit {
  id: number;
  first_name: string | null;
  last_name: string | null;
  has_dob: boolean;
  enrollment_id: number | null;
}

/** An API error that keeps the backend's detail (string or object). */
export class VerifyApiError extends Error {
  status: number;
  detail: unknown;
  constructor(status: number, message: string, detail: unknown) {
    super(message);
    this.status = status;
    this.detail = detail;
  }
  /** The `missing` list a 409 on qualify carries, if any. */
  get missing(): string[] {
    const d = this.detail as { missing?: unknown } | null;
    return d && Array.isArray(d.missing) ? (d.missing as string[]) : [];
  }
}

function messageOf(detail: unknown, fallback: string): string {
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    // FastAPI validation errors: [{loc, msg}]
    const first = detail[0] as { msg?: string; loc?: unknown[] } | undefined;
    if (first?.msg) {
      const field = Array.isArray(first.loc) ? first.loc[first.loc.length - 1] : '';
      return field ? `${String(field)}: ${first.msg}` : first.msg;
    }
  }
  if (detail && typeof detail === 'object' && 'message' in detail) {
    return String((detail as { message: unknown }).message);
  }
  return fallback;
}

async function request<T>(url: string, init: Parameters<typeof authFetch>[1] = {}): Promise<T> {
  const res = await authFetch(url, init);
  if (!res.ok) {
    let detail: unknown = null;
    try {
      const body = await res.json();
      detail = body?.detail ?? body?.message ?? null;
    } catch {
      // not JSON
    }
    throw new VerifyApiError(res.status, messageOf(detail, `${res.status} ${res.statusText}`), detail);
  }
  return (await res.json()) as T;
}

const qs = (params: Record<string, string | number | null | undefined>) => {
  const p = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
  });
  const s = p.toString();
  return s ? `?${s}` : '';
};

const BASE = '/api/verify';

export const verifyApi = {
  overview: () => request<Overview>(`${BASE}/overview`),

  sites: () => request<{ sites: Site[] }>(`${BASE}/sites`),
  site: (id: number) => request<Site>(`${BASE}/sites/${id}`),
  createSite: (body: Record<string, unknown>) =>
    request<Site>(`${BASE}/sites`, { method: 'POST', jsonBody: body }),
  updateSite: (id: number, body: Record<string, unknown>) =>
    request<Site>(`${BASE}/sites/${id}`, { method: 'PATCH', jsonBody: body }),
  addCheck: (id: number, body: { element: SiteElement; criteria_ref?: string; notes?: string }) =>
    request<Site>(`${BASE}/sites/${id}/checks`, { method: 'POST', jsonBody: body }),
  setSiteStatus: (id: number, status: Site['status']) =>
    request<Site>(`${BASE}/sites/${id}/status`, { method: 'POST', jsonBody: { status } }),
  organizations: (q: string) =>
    request<{ organizations: { id: number; name: string }[] }>(`${BASE}/organizations${qs({ q })}`),

  records: (params: { scope?: string; status?: string; site_id?: number; enrollment_id?: number }) =>
    request<{ records: VerifyRecord[] }>(`${BASE}/records${qs(params)}`),
  createRecord: (body: Record<string, unknown>) =>
    request<VerifyRecord>(`${BASE}/records`, { method: 'POST', jsonBody: body }),
  confirmRecord: (id: number) => request<VerifyRecord>(`${BASE}/records/${id}/confirm`, { method: 'POST' }),
  voidRecord: (id: number, reason: string) =>
    request<VerifyRecord>(`${BASE}/records/${id}/void`, { method: 'POST', jsonBody: { reason } }),
  resolveRecord: (
    id: number,
    body: { resolution: 'upheld' | 'corrected' | 'voided'; notes: string; hours?: number; activity_date?: string },
  ) => request<VerifyRecord>(`${BASE}/records/${id}/resolve`, { method: 'POST', jsonBody: body }),

  uploadSheet: (siteId: number, file: File) => {
    const fd = new FormData();
    fd.append('site_id', String(siteId));
    fd.append('file', file);
    return request<SheetBatch>(`${BASE}/sheets`, { method: 'POST', body: fd });
  },
  sheets: () =>
    request<{ sheets: (Pick<SheetBatch, 'id' | 'site_id' | 'site_name' | 'file_name' | 'status' | 'created_at' | 'confirmed_at'> & { row_count: number })[] }>(
      `${BASE}/sheets`,
    ),
  sheet: (id: number) => request<SheetBatch>(`${BASE}/sheets/${id}`),
  sheetImage: async (id: number): Promise<string> => {
    const res = await authFetch(`${BASE}/sheets/${id}/image`);
    if (!res.ok) throw new VerifyApiError(res.status, 'Could not load the sheet photo', null);
    return URL.createObjectURL(await res.blob());
  },
  confirmSheet: (id: number, body: { rows: Record<string, unknown>[]; point_of_contact_name?: string }) =>
    request<{ confirmed: number; batch: SheetBatch }>(`${BASE}/sheets/${id}/confirm`, {
      method: 'POST',
      jsonBody: body,
    }),

  match: () => request<{ matched: number; ambiguous: number }>(`${BASE}/match`, { method: 'POST' }),
  enrollments: (year: number, month: number) =>
    request<{ year: number; month: number; enrollments: EnrollmentRow[] }>(`${BASE}/enrollments${qs({ year, month })}`),
  trackMember: (member_id: number, next_redetermination_date?: string) =>
    request<Enrollment>(`${BASE}/enrollments`, {
      method: 'POST',
      jsonBody: { member_id, next_redetermination_date: next_redetermination_date || undefined },
    }),
  enrollment: (id: number, year: number, month: number) =>
    request<EnrollmentDetail>(`${BASE}/enrollments/${id}${qs({ year, month })}`),
  deliver: (id: number, year: number, month: number) =>
    request<EnrollmentDetail>(`${BASE}/enrollments/${id}/deliver`, { method: 'POST', jsonBody: { year, month } }),
  openRedetermination: (id: number, due_date?: string) =>
    request<EnrollmentDetail>(`${BASE}/enrollments/${id}/redetermination`, {
      method: 'POST',
      jsonBody: { due_date: due_date || undefined },
    }),
  screenExemption: (id: number, exemption_type: string, reason: string) =>
    request<EnrollmentDetail>(`${BASE}/enrollments/${id}/exemption-screen`, {
      method: 'POST',
      jsonBody: { exemption_type, reason },
    }),
  members: (q: string) => request<{ members: MemberHit[] }>(`${BASE}/members${qs({ q })}`),
};

export const todayISO = () => {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};
